import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  BDCPhotoGridCardItem,
  transformToBDCPhotoGridCardItem,
} from '../../bdc-property-detail-dialog/shared/photo-card/photo-card.interface'
import { Store } from '@ngrx/store'
import {
  BdcContentV2Service,
  BdcPropertyPhotoGalleryData,
  BdcPhotoGalleryPhotoItem,
} from '@tokeet-frontend/channel-content'
import { ActionFailed, ConfirmDialogService, Toaster } from '@tokeet-frontend/tv3-platform'
import { finalize, switchMap } from 'rxjs/operators'
import { forkJoin } from 'rxjs'
import * as lodash from 'lodash'
import { PhotoGridComponent } from '../../bdc-property-detail-dialog/shared/photo-grid/photo-grid.component'

@Component({
  selector: 'app-bdc-property-unit-gallery',
  templateUrl: './bdc-property-unit-gallery.component.html',
})
export class BdcPropertyUnitGalleryComponent implements OnInit {
  @ViewChild('photoGrid') photoGrid: PhotoGridComponent

  @Input() propertyId: number
  @Input() roomId: number

  galleryData: BdcPropertyPhotoGalleryData | null = null
  photos: BDCPhotoGridCardItem[] = []
  isLoading = true
  get selectedPhotos() {
    return this.photoGrid?.selectedPhotos
  }

  constructor(
    private store: Store<any>,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private contentService: BdcContentV2Service
  ) {}

  ngOnInit(): void {
    this.onRefresh()
  }

  onRefresh(): void {
    forkJoin({
      gallery: this.contentService.getRoomPhotoGallery(this.propertyId, this.roomId),
      photos: this.contentService.getPropertyPhotos(this.propertyId),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ({ gallery, photos }) => {
          this.galleryData = gallery
          this.photos =
            lodash
              .compact(gallery.order?.map((t) => photos.find((p) => t.photo_id === p.photo_id)))
              .map((t) => transformToBDCPhotoGridCardItem(t)) || []
          this.photos = lodash.sortBy(this.photos, (p) => (p.isMain ? 0 : 1))
          this.selectedPhotos?.clear()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onDeletePhoto(photoId: string): void {
    this.confirm
      .confirm({
        title: $localize`Delete Photo`,
        body: $localize`Are you sure you want to delete this photo from gallery?`,
      })
      .pipe(
        switchMap(() =>
          this.contentService.updateRoomPhotoGallery(this.propertyId, this.roomId, {
            remove_photos: [photoId],
          })
        )
      )
      .subscribe(
        (result) => {
          this.toaster.success($localize`Photo deleted from gallery successfully`)
          this.onRefresh()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onBatchDelete(): void {
    if (this.selectedPhotos.size === 0) return

    this.confirm
      .confirm({
        title: $localize`Delete Selected Photos`,
        body: $localize`Are you sure you want to delete ${this.selectedPhotos.size} selected photos from the gallery?`,
      })
      .pipe(
        switchMap(() =>
          this.contentService.updateRoomPhotoGallery(this.propertyId, this.roomId, {
            remove_photos: Array.from(this.selectedPhotos),
          })
        )
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Selected photos deleted from gallery successfully`)
          this.selectedPhotos.clear()
          this.onRefresh()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  isPhotoSelected(photo: BdcPhotoGalleryPhotoItem): boolean {
    return this.selectedPhotos.has(photo.photo_id)
  }
}
